/**
 * Party funds are stored as a signed integer of copper so a running total is a
 * plain SUM with no rounding drift. Display converts back to gp/sp/cp.
 */
export const COIN_VALUES = { pp: 1000, gp: 100, ep: 50, sp: 10, cp: 1 } as const
export type CoinType = keyof typeof COIN_VALUES

export const COIN_NAMES: Record<CoinType, string> = {
  pp: 'platinum', gp: 'gold', ep: 'electrum', sp: 'silver', cp: 'copper',
}

export const toCopper = (amount: number, coin: CoinType): number =>
  Math.round(amount * COIN_VALUES[coin])

/** Breaks copper into the largest denominations, skipping electrum. */
export function splitCoins(copper: number): { gp: number; sp: number; cp: number } {
  const negative = copper < 0
  let left = Math.abs(Math.trunc(copper))

  const gp = Math.floor(left / 100); left -= gp * 100
  const sp = Math.floor(left / 10); left -= sp * 10

  const sign = negative ? -1 : 1
  return { gp: gp * sign, sp: sp * sign, cp: left * sign }
}

/** "12 gp 4 sp", or "0 gp" when empty. Always signed for ledger deltas. */
export function formatCopper(copper: number, options: { sign?: boolean } = {}): string {
  const { gp, sp, cp } = splitCoins(copper)
  const parts = [
    gp !== 0 && `${Math.abs(gp)} gp`,
    sp !== 0 && `${Math.abs(sp)} sp`,
    cp !== 0 && `${Math.abs(cp)} cp`,
  ].filter(Boolean) as string[]

  const body = parts.length ? parts.join(' ') : '0 gp'
  if (!options.sign) return copper < 0 ? `-${body}` : body
  return copper < 0 ? `-${body}` : `+${body}`
}
